import axios from "@/lib/axios.ts";
import type { CustomAxiosRequestConfig } from "@/interfaces/customAxiosRequestConfig";
import { NewDataUser, PendingRequest } from "@/modules/pendingRequests/interfaces/pendingRequest.ts";
import { useQuery } from "@tanstack/vue-query";
import type { PaginatedResponse } from "@/interfaces/paginatedResponse.ts";
import { useUserApprovalsStore } from "@/stores/userApprovals.ts";
import { storeToRefs } from "pinia";
import { computed, watch, ref, toRef } from "vue";
import type { MaybeRefOrGetter } from "vue";

interface Props {
  /**
   * Acepta un getter/ref además de un booleano suelto: quien depende de los
   * permisos del usuario no los tiene resueltos al montar, y con un booleano
   * plano la consulta quedaba deshabilitada para siempre.
   */
  immediate?: MaybeRefOrGetter<boolean>;
  params: Params;
  errorHandling?: boolean;
}

interface Params {
  type?: string;
  company: number | string | undefined;
  // Búsqueda por datos del usuario solicitado (nombre/apellidos/correo/username).
  search?: string;
}

const getUserApprovals = async (
  params: Params,
  page?: number,
  errorHandling = true
): Promise<PaginatedResponse<PendingRequest<NewDataUser>>> => {
  const { data } = await axios.get<PaginatedResponse<PendingRequest<NewDataUser>>>(
    `/api/users/approvals?page=${page}`,
    { params, errorHandling } as CustomAxiosRequestConfig
  );
  return data;
};

const useUserApprovals = (props: Props) => {
  const { immediate = true, params, errorHandling = true } = props;
  const { setUsers, setPagination, setPage } = useUserApprovalsStore();
  const { users, pages, pagination } = storeToRefs(useUserApprovalsStore());

  // Make company + search reactive
  const company = ref(params.company);
  const search = ref(params.search);

  const createQuery = (type: string) => {
    return useQuery({
      queryKey: computed(() => ["userApprovals", type, company.value, search.value, pages.value[type]]),
      queryFn: () => getUserApprovals({ company: company.value, type, search: search.value }, pages.value[type], errorHandling),
      enabled: toRef(immediate),
      retry: false,
    });
  };

  const pendingQuery = createQuery("pending");
  const approvedQuery = createQuery("approved");
  const rejectedQuery = createQuery("rejected");

  // Sincronizar datos con el store cuando cambien las respuestas
  const syncResponse = (type: string, query: any) => {
    watch(
      () => query.data.value,
      (response) => {
        if (response) {
          setUsers(type, response.data);
          setPagination(type, response);
        }
      },
      { immediate: true }
    );
  };

  syncResponse("pending", pendingQuery);
  syncResponse("approved", approvedQuery);
  syncResponse("rejected", rejectedQuery);

  // Watch for changes in company and refetch
  watch(
    () => params.company,
    (newCompany) => {
      if (newCompany !== company.value) {
        company.value = newCompany;
        refetchAll();
      }
    }
  );

  // Watch for changes in the user search term and refetch
  watch(
    () => params.search,
    (newSearch) => {
      if (newSearch !== search.value) {
        search.value = newSearch;
        refetchAll();
      }
    }
  );

  // Watch for changes in pages and refetch
  watch(
    () => ({ ...pages.value }),
    (newValue, oldValue) => {
      Object.keys(newValue).forEach((type) => {
        if (newValue[type] !== oldValue[type]) {
          if (type === "pending") {
            pendingQuery.refetch();
          }
          if (type === "approved") {
            approvedQuery.refetch();
          }
          if (type === "rejected") {
            rejectedQuery.refetch();
          }
        }
      });
    }
  );

  // Computed para saber si alguna petición está en carga o en refetch
  const isLoading = computed(() =>
    pendingQuery.isLoading.value || approvedQuery.isLoading.value || rejectedQuery.isLoading.value
  );
  const isRefetching = computed(() =>
    pendingQuery.isRefetching.value || approvedQuery.isRefetching.value || rejectedQuery.isRefetching.value
  );

  const refetchAll = () => {
    pendingQuery.refetch();
    approvedQuery.refetch();
    rejectedQuery.refetch();
  };

  return {
    userApprovals: users,
    paginations: pagination,
    pages,
    setPage,
    refetchAll,
    isLoading,
    isRefetching,
    setCompany: (newCompany: number | string | undefined) => {
      company.value = newCompany;
    }
  };
};

export default useUserApprovals;
